// src/contexts/AuthContext.jsx
import React, { createContext, useState, useEffect, useCallback } from 'react';
import {
  isAuthenticated,
  verifyToken,
  getCurrentUser as getCurrentUserService,
  login as apiLogin,
  register as apiRegister,
  logout as apiLogout
} from '../api/authService';

// Création du contexte
export const AuthContext = createContext();

// Fournisseur du contexte
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Vérifier l'authentification au chargement
  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (isAuthenticated()) {
          const response = await verifyToken();
          if (response.success) {
            try {
              const userResponse = await getCurrentUserService();
              if (userResponse && userResponse.user) {
                setUser(userResponse.user);
                setIsAuth(true);
              } else {
                handleLogout();
              }
            } catch (error) {
              // Fallback: try localStorage profile
              const storedProfile = localStorage.getItem('elemental_duelists_user_profile');
              if (storedProfile) {
                try {
                  const profile = JSON.parse(storedProfile);
                  setUser(profile);
                  setIsAuth(true);
                } catch (e) {
                  handleLogout();
                }
              } else {
                handleLogout();
              }
            }
          } else {
            // Token non valide — auto-login as guest in dev mode
            await autoGuestLogin();
          }
        } else {
          // No token — auto-login as guest for seamless UX
          await autoGuestLogin();
        }
      } catch (error) {
        console.error('Erreur d\'authentification:', error);
        await autoGuestLogin();
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Auto-login as guest player (seamless demo experience)
  const autoGuestLogin = async () => {
    try {
      const api = (await import('../api/api')).default;
      const response = await api.post('/auth/guest');
      if (response.data && response.data.success && response.data.token) {
        localStorage.setItem('elemental_duelists_auth_token', JSON.stringify(response.data.token));
        localStorage.setItem('elemental_duelists_user_profile', JSON.stringify(response.data.user));
        setUser(response.data.user);
        setIsAuth(true);
        return;
      }
    } catch (err) {
      console.warn('[AUTH] Server guest login failed, using local guest:', err.message);
    }
    // Fallback: local guest
    const guestUser = {
      id: 'guest_player',
      username: 'Joueur',
      email: 'guest@elementum.local',
      role: 'user',
      isGuest: true
    };
    const guestToken = 'guest_token_' + Date.now();
    localStorage.setItem('elemental_duelists_auth_token', JSON.stringify(guestToken));
    localStorage.setItem('elemental_duelists_user_profile', JSON.stringify(guestUser));
    setUser(guestUser);
    setIsAuth(true);
  };

  // Login
  const login = useCallback(async (credentials) => {
    setError(null);
    try {
      const response = await apiLogin(credentials.email || credentials.username, credentials.password);
      if (response.success) {
        setUser(response.user);
        setIsAuth(true);
      }
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Register (R35 : birthDate optionnelle — age-gating 18+)
  const register = useCallback(async (userData) => {
    setError(null);
    try {
      const response = await apiRegister(userData.username, userData.email, userData.password, userData.birthDate || null);
      if (response.success) {
        setUser(response.user);
        setIsAuth(true);
      }
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Login with Google (GIS) — le credential est déjà traité côté service
  const loginWithGoogle = useCallback(async () => {
    setError(null);
    try {
      const { loginWithGoogle: googleLogin } = require('../api/authService');
      const response = await googleLogin();
      if (response.success) {
        setUser(response.user);
        setIsAuth(true);
      }
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Gérer la déconnexion
  const handleLogout = useCallback(() => {
    apiLogout();
    setUser(null);
    setIsAuth(false);
  }, []);

  // Valeur du contexte
  const authContextValue = {
    user,
    currentUser: user, // alias for GameContext compatibility
    setUser,
    isAuthenticated: isAuth,
    setIsAuthenticated: setIsAuth,
    login,
    register,
    loginWithGoogle,
    logout: handleLogout,
    // R35 : rafraîchit /auth/me (ex: après enregistrement de la date de naissance)
    refreshUser: async () => {
      try {
        const r = await getCurrentUserService();
        // R51 : getCurrentUser retourne {user} (sans success) — les deux formes acceptées
        if (r && r.user) setUser(r.user);
        return r;
      } catch (e) { return null; }
    },
    error,
    loading
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};
